import { useCallback, useEffect, useState } from 'react'
import { fetchJSON, FetchJSONResult } from '../utils/fetchJSON'

export type FetchQueryResult<T> = {
  /**
   * The fetched data, or `undefined` if not yet loaded or in case of an error.
   */
  data: FetchJSONResult<T>['data']
  /**
   * A boolean indicating whether the fetch is currently in progress.
   */
  loading: boolean
  /**
   * Any error encountered during the fetch defined by a status code outside the
   * 200 - 299 range, or `undefined` if no error has occurred.
   */
  error?: FetchJSONResult<T>['error']
  /**
   * A function to manually reload the data.  Optionally, you can pass an
   * `AbortController` to cancel the request.
   */
  reload: (abortController?: AbortController) => Promise<void>
}

export interface UseFetchHookOptions<T> {
  /**
   * The URL to fetch data from.
   */
  url: string
  /**
   * Optional configuration for the fetch request.  Corresponds to the
   * `RequestInit` object used by the Fetch API.
   */
  requestInit?: RequestInit
  /**
   * If true, the request will be skipped.
   * Useful for conditionally preventing a fetch call.
   */
  skip?: boolean
  /**
   * Optional callback that will be executed when the fetch completes successfully with no errors.
   *
   * @param data The fetched data.
   */
  onCompleted?: (data: T) => void
  /**
   * Optional callback that will be executed if the fetch fails.
   *
   * @param data The error response
   */
  onError?: (data: Error) => void
}

/**
 * A custom hook to perform RESTful JSON fetches, similar to Apollo's `useQuery` hook.
 *
 * Features:
 * - Automatically handles loading, error, and data states.
 * - Allows conditional fetching using the `skip` option.
 * - Provides callbacks for handling side effects on success (`onCompleted`) or failure (`onError`).
 * - Only primitive options (`url`, `skip`) are reactive. Non-primitive options like `requestInit`,
 *   `onError`, and `onCompleted` are not reactive.
 *
 * @example
 * // Basic usage:
 * const { data, loading, error, reload } = useFetch<User[]>({ url: '/api/users' });
 *
 * @example
 * // Conditional fetching:
 * const { data } = useFetch<User>({ url: `/api/${projectId}/details`, skip: !projectId });
 *
 * @example
 * // Handling side effects:
 * useFetch<User>({
 *   url: '/api/user',
 *   onCompleted: (data) => console.log('Fetch successful:', data),
 *   onError: (err) => console.error('Fetch failed:', err),
 * });
 */
export function useFetch<T = unknown>({
  url,
  skip,
  requestInit,
  onError,
  onCompleted,
}: UseFetchHookOptions<T>): FetchQueryResult<T> {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(!skip)

  const fetchData = useCallback(
    async (abortController?: AbortController) => {
      if (!url || skip) return

      setLoading(true)
      setError(undefined)

      try {
        const { data, error } = await fetchJSON<T>({
          url,
          requestInit: {
            signal: abortController?.signal,
            ...requestInit,
          },
        })
        if (error) {
          throw error
        }
        setData(data)
        if (onCompleted) {
          onCompleted(data!)
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err)
          if (onError) {
            onError(err)
          }
        }
      } finally {
        setLoading(false)
      }
    },
    // Only make primitive values reactive to avoid constant re-rendering due to
    // changed references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url, skip]
  )

  useEffect(() => {
    const controller = new AbortController()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    fetchData(controller)

    return () => {
      controller.abort()
    }
  }, [url, fetchData])

  return { data, error, loading, reload: fetchData }
}
